import { useEffect } from 'react';
import { useApolloClient } from '@apollo/client';
import { Socket } from 'socket.io-client';
import {
    createOrderChangedHandler,
    INTERNAL_ORDER_DETAILS_CHANGED_EVENT,
    ORDER_CHANGED_EVENT,
    OrderChangedEvent,
} from 'apollo/order-live-updates';

/**
 * Keeps the order views live (DD-3231), mounted once at the app root:
 * - ORDER_CHANGED refreshes the order in the Apollo cache, which re-renders whatever shows it
 * - INTERNAL_ORDER_DETAILS_CHANGED refreshes the lab's internal notes and files of an order
 * - a reconnect the server could not recover (connection-state recovery replays missed
 *   events for up to two minutes) re-reads every live view, since events were lost
 */
export const useOrderChangedEvents = (socket: Socket | null) => {
    const client = useApolloClient();

    useEffect(() => {
        if (!socket) return;
        const handler = createOrderChangedHandler(client);
        let hasConnected = socket.connected;
        const onOrderChanged = (event: OrderChangedEvent) => handler.handle(event);
        const onInternalDetailsChanged = (event: { orderId?: string }) => handler.handleInternalDetails(event);
        const onConnect = () => {
            const isReconnect = hasConnected;
            hasConnected = true;
            if (isReconnect && !socket.recovered) {
                handler.handleReconnect();
            }
        };
        socket.on(ORDER_CHANGED_EVENT, onOrderChanged);
        socket.on(INTERNAL_ORDER_DETAILS_CHANGED_EVENT, onInternalDetailsChanged);
        socket.on('connect', onConnect);
        return () => {
            socket.off(ORDER_CHANGED_EVENT, onOrderChanged);
            socket.off(INTERNAL_ORDER_DETAILS_CHANGED_EVENT, onInternalDetailsChanged);
            socket.off('connect', onConnect);
            handler.dispose();
        };
    }, [socket, client]);
};
